import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, LinearProgress,
  Fade, Backdrop, Paper, Chip, Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// ─── Tutorial Steps ──────────────────────────────────────────────────────────
// Organized to match the actual SFM nav structure and live feature set.
// Routes correspond exactly to paths in nav/config.js.
const STEPS = [
  // ── Getting Started ────────────────────────────────────────────────────────
  {
    id: 'welcome',
    module: 'Getting Started',
    title: 'Welcome to SFM',
    body: 'Shop Floor Management (SFM) is your end-to-end fabrication traceability and quality platform. It connects Purchase Orders, Inventory, Jobs, Tasks, QA/NCR, WeldAssure, and Shipping into one unbroken audit trail — so every piece of steel can be traced from the Certified Mill Test Report (CMTR) to the Bill of Lading.',
    route: '/dashboard/app',
    icon: '👋',
  },
  {
    id: 'dashboard',
    module: 'Getting Started',
    title: 'Your Dashboard',
    body: 'The Dashboard gives you a live pulse on the shop floor: Active Jobs, Pending Tasks, Approval Rate, QA holds, and Inventory on hand. The KPI cards update in real time as your team records events. Use the sidebar on the left to navigate between all modules.',
    route: '/dashboard/app',
    icon: '📊',
  },
  {
    id: 'nav-overview',
    module: 'Getting Started',
    title: 'How SFM Is Organized',
    body: 'The left sidebar has five main sections: SFM Operations (Jobs, Tasks, Processes, Inventory, POs, BOL, Documents, Kiosk), WeldAssure (Heat Management, Release Packages, Welder & WPS/PQR/WQTR records), SFM Inspection Plus (QA / NCR), Contacts, and Reports. Admin and Settings are at the bottom. Each section is permission-controlled.',
    route: '/dashboard/app',
    icon: '🗂️',
  },

  // ── Module 00 — First-Time Setup ───────────────────────────────────────────
  {
    id: 'setup-company',
    module: 'Module 00 — Setup',
    title: 'Step 1: Create Your Company',
    body: 'Before creating jobs or receiving material, register your organization. Go to Contacts → Companies → Add New Company. Enter your fabricator\'s legal name, address, and contact details. This company record appears on Bills of Lading, reports, and traceability documents.',
    route: '/companies',
    icon: '🏢',
  },
  {
    id: 'setup-vendor',
    module: 'Module 00 — Setup',
    title: 'Step 2: Add Your Steel Suppliers',
    body: 'Go to Contacts → Vendors → Add New Vendor. Add each steel supplier (e.g. "DT Steel", "Metals Depot"). The Vendor links to your Purchase Orders and Certified Mill Test Reports, completing the supply-chain traceability required by AWS D1.1 and AISC 360.',
    route: '/vendors',
    icon: '🏭',
  },
  {
    id: 'setup-users',
    module: 'Module 00 — Setup',
    title: 'Step 3: Create Your Team',
    body: 'Go to Admin → Users → New User. Create accounts for each team member and assign their role: Receiver, Shop Foreman, Fitter, Welder, Painter, QC Inspector, Project Manager, or Kiosk User. The Kiosk User role is used on iPads on the shop floor for touch-based stage tracking.',
    route: '/users',
    icon: '👥',
  },
  {
    id: 'setup-roles',
    module: 'Module 00 — Setup',
    title: 'Step 4: Review Roles & Permissions',
    body: 'SFM ships with pre-configured roles covering every shop floor function. Go to Admin → Roles to view and customize the permission matrix. Each role controls which modules, pages, and actions a user can access. You can fine-tune permissions without writing code — changes take effect on the user\'s next login.',
    route: '/roles',
    icon: '🔐',
  },

  // ── Module 01 — Purchase Orders & Receiving ────────────────────────────────
  {
    id: 'po-create',
    module: 'Module 01 — Receiving',
    title: 'Step 5: Create a Purchase Order',
    body: 'Before receiving steel, create a Purchase Order. Go to SFM Operations → Purchase Orders → New Purchase Order. Select your Company and Vendor, enter the PO number, and add line items for each steel profile (e.g. PLT26×1200, E450 grade, qty 1200 kg). The PO is the foundation for all material traceability.',
    route: '/purchaseorder',
    icon: '📋',
  },
  {
    id: 'po-receive',
    module: 'Module 01 — Receiving',
    title: 'Step 6: Receive Material & Attach CMTRs',
    body: 'When steel arrives, open the PO and click Receive. Enter the heat number (e.g. H-E450-001) from the Certified Mill Test Report (CMTR) and attach the CMTR document. If the CMTR is missing, the heat is automatically flagged At Risk — it will be blocked from shipping until the certificate is uploaded.',
    route: '/purchaseorder',
    icon: '📦',
  },

  // ── Module 02 — Inventory ──────────────────────────────────────────────────
  {
    id: 'inventory-ledger',
    module: 'Module 02 — Inventory',
    title: 'Step 7: The Heat Ledger',
    body: 'After receiving, every heat appears in SFM Operations → Inventory. The Heat Ledger shows quantity received, issued, consumed, and balance on hand per heat number — updated in real time as pieces are cut and issued to jobs. Click any heat to see its full transaction history and attached CMTR.',
    route: '/inventory',
    icon: '📊',
  },
  {
    id: 'inventory-atrisk',
    module: 'Module 02 — Inventory',
    title: 'At-Risk Heat Flags',
    body: 'Heats with a missing CMTR, duplicate heat ID, or grade discrepancy are flagged At Risk with a red indicator in the Heat Ledger. An at-risk heat cannot be included on a Bill of Lading until the issue is resolved. This protects you from inadvertently shipping non-conforming steel to site.',
    route: '/inventory',
    icon: '⚠️',
  },

  // ── Module 03 — Jobs & Processes ──────────────────────────────────────────
  {
    id: 'jobs-create',
    module: 'Module 03 — Jobs',
    title: 'Step 8: Create a Job',
    body: 'A Job represents a fabrication contract or project. Go to SFM Operations → Jobs → New Job. Enter the job name (e.g. "KWT-1GC1 Box Column"), client, linked Purchase Order, and start/end dates. The Job is the parent record for all Processes (erection sequences), Tasks, material issues, QA inspections, and shipping loads.',
    route: '/jobs',
    icon: '🔧',
  },
  {
    id: 'processes-create',
    module: 'Module 03 — Jobs',
    title: 'Step 9: Create Processes (Erection Sequences)',
    body: 'A Process is an erection sequence — a bundle of piece marks that move through the shop together and ship to the jobsite to be erected as a unit. Each Process contains Tasks (the production gates: Vendor Control, Cutting, Fabrication, Welding, Painting, Shipping) that the whole bundle advances through simultaneously. Processes also drive load planning — BOL pieces are grouped by Process. Go to SFM Operations → Processes → New Process and link it to the Job.',
    route: '/sequence',
    icon: '🏗️',
  },
  {
    id: 'jobs-issue',
    module: 'Module 03 — Jobs',
    title: 'Step 10: Issue Material to Piece Marks',
    body: 'Within a Job, open a piece mark (fabricated item) and issue material from the Heat Ledger. SFM enforces heat-to-piece traceability: the grade of the issued heat must match the grade required by the piece mark. A grade mismatch is rejected. This creates the permanent, auditable link between the CMTR and the fabricated part.',
    route: '/jobs',
    icon: '🔗',
  },

  // ── Module 04 — Tasks ─────────────────────────────────────────────────────
  {
    id: 'tasks',
    module: 'Module 04 — Tasks',
    title: 'Step 11: Manage Tasks',
    body: 'Tasks are the production gate records within a Process — Vendor Control, Cutting, Fabrication, Welding, Painting, and Shipping. Each Task is assigned to a fabrication team member and tracks a specific gate for a piece mark. The entire Process advances through these gates together. Go to SFM Operations → Tasks to see your team\'s workload. QC Inspectors can approve or reject tasks; a rejection returns the task with a reason and triggers an in-app notification.',
    route: '/tasks',
    icon: '✅',
  },

  // ── Module 05 — Documents ─────────────────────────────────────────────────
  {
    id: 'documents',
    module: 'Module 05 — Documents',
    title: 'Step 12: Upload Engineering Documents',
    body: 'Go to SFM Operations → Documents to bulk-upload engineering packages, IFC drawings, shop drawings, and submittals. Documents are auto-matched to Jobs and Piece Marks by filename convention. Uploaded documents are accessible to your entire team from within the relevant job or piece mark record.',
    route: '/documents/upload',
    icon: '📁',
  },

  // ── Module 06 — WeldAssure ────────────────────────────────────────────────
  {
    id: 'weldassure-heat',
    module: 'Module 06 — WeldAssure',
    title: 'Heat Management',
    body: 'The WeldAssure section manages your welding quality program. Heat Management lets you log incoming heats, attach and verify CMTRs, and manage quarantine/release status — all tied to a specific Job. A heat must be verified and released before it can be issued to a weld joint. This enforces AWS D1.1 §6 material qualification at the source.',
    route: '/heat-management',
    icon: '🔥',
  },
  {
    id: 'weldassure-release',
    module: 'Module 06 — WeldAssure',
    title: 'Release Packages',
    body: 'Release Packages are engineering control gates. A Release Package groups a set of fabricated items and must be activated (released) by an authorized engineer before fabrication of those items can begin. Go to WeldAssure → Release Packages → New Package, link it to a Job, add items, and activate it to authorize work.',
    route: '/release-packages',
    icon: '📦',
  },
  {
    id: 'weldassure-docs',
    module: 'Module 06 — WeldAssure',
    title: 'Welders, WPS, PQR & WQTR Records',
    body: 'WeldAssure maintains your complete welding documentation library: the Welder Directory (names and stamp IDs), WPS (Welding Procedure Specifications per AWS D1.1), PQR (Procedure Qualification Records), and WQTR (Welder Qualification Test Records). These records are the backbone of your weld quality program and are required for code-compliance audits.',
    route: '/welders',
    icon: '🪛',
  },

  // ── Module 07 — QA / NCR ──────────────────────────────────────────────────
  {
    id: 'qa-ncr',
    module: 'Module 07 — QA / NCR',
    title: 'Step 13: QA Inspections & Non-Conformance Reports',
    body: 'Go to SFM Inspection Plus → QA / NCR to manage all quality records. Raise an NCR to capture a non-conformance: description, location, root cause, corrective action (CAPA), photos, and re-inspection date. NCR status flows through Raised → CAPA In Progress → Closed. A piece on NCR hold cannot advance stages or ship until the NCR is closed and re-inspection passes.',
    route: '/qa-ncr',
    icon: '📝',
  },

  // ── Module 08 — Kiosk ─────────────────────────────────────────────────────
  {
    id: 'kiosk',
    module: 'Module 08 — Kiosk',
    title: 'Kiosk Mode (Shop Floor)',
    body: 'Go to SFM Operations → Kiosk View for the touch-optimized shop floor interface, designed for glove-friendly use on iPads. Workers scan or select a piece mark QR code and log their station: Cut → Fit → Weld → Paint → Final Inspection. If a piece is on QC hold, the kiosk immediately shows a red HOLD screen and blocks the scan. Events sync automatically when connectivity returns.',
    // Kiosk lives in its own full-screen layout OUTSIDE the dashboard shell that
    // hosts this tour; navigating there unmounts the tour mid-run (was the 'crash
    // at 20 of 25'). Keep route null so we describe Kiosk in place and the tour survives.
    route: null,
    icon: '📱',
  },

  // ── Module 09 — Shipping ──────────────────────────────────────────────────
  {
    id: 'shipping-plan',
    module: 'Module 09 — Shipping',
    title: 'Step 14: Plan a Load',
    body: 'When pieces are QC-cleared and ready to ship, go to SFM Operations → Bill of Lading → New Bill. Select the Job and Process (ship group), then add piece marks to the load. SFM calculates the total weight. Pieces on QC hold or with at-risk heats are blocked from the load automatically — enforcing a clean chain of custody to the site.',
    route: '/shipping-list',
    icon: '🚚',
  },
  {
    id: 'shipping-bol',
    module: 'Module 09 — Shipping',
    title: 'Bill of Lading',
    body: 'Finalize the load to generate the Bill of Lading. The BOL lists all piece marks, their process, weights, and carrier information — ready to print and hand to the driver or email to the General Contractor. Every BOL is permanently stored and tied to the job\'s audit trail. Previously shipped loads cannot be modified.',
    route: '/shipping-list',
    icon: '📃',
  },

  // ── Module 10 — Reports ───────────────────────────────────────────────────
  {
    id: 'reports',
    module: 'Module 10 — Reports',
    title: 'Step 15: Traceability Reports',
    body: 'Go to Reports to access the full traceability document suite. The Piece Trace Report shows the complete chain for any piece: Heat number → CMTR status → Issue date → Fabrication stages → NCR history → BOL date. This is the document your General Contractor, Engineer of Record, or code inspector will request at project close-out.',
    route: '/reports',
    icon: '🔍',
  },

  // ── Complete ──────────────────────────────────────────────────────────────
  {
    id: 'complete',
    module: 'Complete',
    title: "You're Ready to Go!",
    body: 'You now know the full SFM workflow: Setup → Purchase Orders → Inventory → Jobs → Tasks → WeldAssure → QA/NCR → Kiosk → Shipping → Reports. Every step creates an immutable, auditable record. Start by creating your Company, adding a Vendor, and setting up your Users. You can replay this tour any time from Settings → App Preferences.',
    route: '/dashboard/app',
    icon: '🎉',
  },
];

const STORAGE_KEY = 'sfm_onboarding_complete';
const STORAGE_VERSION = 'v2';

// ─── Component ────────────────────────────────────────────────────────────────
export default function OnboardingTutorial() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Respect the app preferences toggle (defaults to enabled if key absent)
    const enabledFlag = localStorage.getItem('sfm_onboarding_enabled');
    if (enabledFlag === 'false') return;

    const done = localStorage.getItem(STORAGE_KEY);
    if (done !== STORAGE_VERSION) {
      const timer = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleComplete = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setOpen(false);
      setExiting(false);
      localStorage.setItem(STORAGE_KEY, STORAGE_VERSION);
    }, 300);
  }, []);

  const handleNext = useCallback(() => {
    const nextStep = step + 1;
    if (nextStep >= STEPS.length) {
      handleComplete();
      return;
    }
    setStep(nextStep);
    const route = STEPS[nextStep].route;
    if (route) navigate(route);
  }, [step, navigate, handleComplete]);

  const handleBack = useCallback(() => {
    const prevStep = Math.max(0, step - 1);
    setStep(prevStep);
    const route = STEPS[prevStep].route;
    if (route) navigate(route);
  }, [step, navigate]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  if (!open) return null;

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  // Group modules for progress indicator
  const modules = [...new Set(STEPS.map(s => s.module))];
  const currentModuleIdx = modules.indexOf(current.module);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: 9999,
        // 20% opacity dark backdrop — screen content is clearly visible behind
        backgroundColor: 'rgba(0, 0, 0, 0.20)',
        backdropFilter: 'none',
      }}
    >
      <Fade in={open && !exiting}>
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 0, sm: 40 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '100%', sm: 580 },
            maxWidth: '100%',
            zIndex: 10000,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              borderRadius: { xs: '20px 20px 0 0', sm: '20px' },
              border: '1px solid rgba(0,0,0,0.08)',
              // 80% white — solid, clean, highly readable
              backgroundColor: 'rgba(255, 255, 255, 0.80)',
              backdropFilter: 'blur(12px) saturate(180%)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
              overflow: 'hidden',
            }}
          >
            {/* Progress bar */}
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 3,
                backgroundColor: 'rgba(0,122,255,0.12)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#007AFF',
                  borderRadius: 2,
                  transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                },
              }}
            />

            <Box sx={{ p: 3 }}>
              {/* Header row */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Chip
                  label={current.module}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(0,122,255,0.10)',
                    color: '#007AFF',
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 500 }}>
                  {step + 1} / {STEPS.length}
                </Typography>
              </Stack>

              {/* Icon + Title */}
              <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
                <Box sx={{ fontSize: 30, lineHeight: 1, mt: 0.3, flexShrink: 0 }}>{current.icon}</Box>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: 19, color: 'text.primary' }}>
                  {current.title}
                </Typography>
              </Stack>

              {/* Body */}
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  fontSize: 14.5,
                  mb: 3,
                }}
              >
                {current.body}
              </Typography>

              {/* Module progress indicator */}
              <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
                {modules.map((mod, i) => (
                  <Box
                    key={mod}
                    sx={{
                      flex: 1,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: i <= currentModuleIdx
                        ? '#007AFF'
                        : 'rgba(120,120,128,0.20)',
                      transition: 'background-color 0.3s ease',
                    }}
                  />
                ))}
              </Stack>

              {/* Actions */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Button
                  variant="text"
                  onClick={handleSkip}
                  sx={{
                    color: 'text.disabled',
                    fontSize: 13.5,
                    fontWeight: 500,
                    '&:hover': { color: 'text.secondary', bgcolor: 'transparent' },
                  }}
                >
                  Skip tour
                </Button>

                <Stack direction="row" spacing={1}>
                  {!isFirst && (
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      size="medium"
                      sx={{
                        borderRadius: 20,
                        borderColor: 'rgba(120,120,128,0.35)',
                        color: 'text.primary',
                        fontWeight: 600,
                        px: 2.5,
                        '&:hover': { borderColor: 'rgba(0,0,0,0.25)', bgcolor: 'rgba(0,0,0,0.04)' },
                      }}
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    size="medium"
                    disableElevation
                    sx={{
                      borderRadius: 20,
                      backgroundColor: '#007AFF',
                      fontWeight: 600,
                      px: 3,
                      boxShadow: '0 4px 14px rgba(0,122,255,0.35)',
                      '&:hover': {
                        backgroundColor: '#0066CC',
                        boxShadow: '0 4px 16px rgba(0,122,255,0.45)',
                      },
                    }}
                  >
                    {isLast ? 'Get Started' : 'Next →'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Backdrop>
  );
}

export { STORAGE_KEY, STORAGE_VERSION };
